import moment from 'moment';
import * as Yup from 'yup';
import { i18n } from '../../../../../language';
import { initValue } from '../../../../../utils';
import { checkSubmitData } from '../../../../../utils/check';
import { encryptHkid } from '../../../../../utils/newBedStatusUtil';
import { bsAttentionFunc, bsMlookupListFunc } from './legacyBedStatusAdapter';

export const P = {
  patienID: 'caseNo',
  hospitalCode: 'fromHospCode', // *
  name: 'patName',
  age: 'age',
  specialtyCode: 'eisSpecCd',
  gender: 'patGender',
  ward: 'fromWardLocation',
  bedNo: 'fromBedNo',
  date_time: 'targetArriveDtm',
  admissionType: 'admissionType',
  assignedBedNo: 'assignedBedNo',
  principleDiagnosis: 'principleDiagnosis',
  principleDiagnosisText: 'diagnosis',
  principleDiagnosisInputValue: 'principleDiagnosisInputValue',
  onVentilator: 'onVentilator',
  egPercent: 'ventilatorPrecentage',
  onOxygen: 'onOxygen',
  tracheotomy: 'tracheotomy',
  egL: 'oxygenLitre',
  airbornePrecaution: 'airbornePrecautionYn',
  dropletPrecaution: 'dropletPrecautionYn',
  contactPrecaution: 'contactPrecautionYn',
  reverseLsolation: 'reversePrecautionYn',
  covid19: 'covidPositiveYn',
  covidPositivePat: 'covidPositivePat',
  attention: 'attention',
  remarks: 'remark',
  date: 'date',
  time: 'time',
  admDtm: 'admDtm',
  status: 'status',
  counter: 'counter',
  covidDate: 'reportDtm',
  covidReportDtm: 'covidReportDtm',
  wardCd: 'wardCd',
  destHospCode: 'destHospCode',
  destWard: 'destWard',
  destWardId: 'destWardId',
  destSpecialty: 'destSpecialty',
  bedNoTransfer: 'bedNo',
  ccHosp: 'ccHosp',
  ccWardLocation: 'ccWardLocation',
  patientKey: 'patientKey',
  hkid: 'hkid',
  chiName: 'chiName',
  destWardLocation: 'destWardLocation',
  cubicleId: 'cubicleId',
};

export const alertText = (value) => {
  if (value === 1) {
    return i18n.t('BEDSTATUS_NO_INFORMATION_IS_RETRIEVED');
  } else {
    return i18n.t('BEDSTATUS_RETRIEVE_SUCCESSFULLY');
  }
};

// 当转出的病人在Completed、Accepted、Deleted、Recv. Ward Ready这四种状态的时候是不能修改信息的
export const saveBtnDisabled = (status) => status === 'C' || status === 'A' || status === 'D' || status === 'RWR';

// 根据现有 transfer-in 资料生成初始备注。
const handleInitRemark = (allData) => {
  if (allData?.[P.covidPositivePat]) {
    return `[nCoV] CT Value: ${allData?.[P.covidPositivePat]?.[0].ctValue}  ${
      allData?.[P.covidPositivePat]?.[0].covidDescription
    }`;
  } else {
    return allData?.[P.remarks] ?? '';
  }
};

const handleInitStatus = (allData, bsMlookupList) => {
  if (allData) {
    return initValue(allData?.[P.status]);
  } else {
    return bsMlookupListFunc.isTypeLookupValueExist(bsMlookupList, 'bs_book_status', 'P') ? 'P' : '';
  }
};

const initTempSchema = (newTemp, schema) => ({
  [P.name]: newTemp[P.name] ? schema[P.name] : null,
  [P.chiName]: newTemp[P.chiName] ? schema[P.chiName] : null,
  [P.gender]: newTemp[P.gender] ? schema[P.gender] : null,
  [P.age]: newTemp[P.age] !== null ? schema[P.age] : null,
  [P.date]: newTemp[P.admDtm] ? schema[P.date] : null,
  [P.time]: newTemp[P.admDtm] ? schema[P.time] : null,
});

const initMoment = (value) => {
  if (value) {
    return moment(value);
  }
  return null;
};

const initRemark = (props) => {
  if (props) {
    return `[nCoV] CT Value: ${props?.[0].ctValue}  ${props?.[0].covidDescription}`;
  } else {
    return '';
  }
};

// 生成 transfer-in popup 的初始表单资料。
export const buildInitFormData = (allData, bsMlookupList) => ({
  [P.patienID]: initValue(allData?.[P.patienID]),
  [P.hospitalCode]: allData?.[P.hospitalCode] ? allData?.[P.hospitalCode] : initValue(allData?.[P.ccHosp]),
  [P.name]: initValue(allData?.[P.name]),
  [P.age]: initValue(allData?.[P.age]),
  [P.specialtyCode]: initValue(allData?.[P.specialtyCode]),
  [P.gender]: initValue(allData?.[P.gender], 'M'),
  [P.ward]: allData?.[P.ward] ? allData?.[P.ward] : initValue(allData?.[P.ccWardLocation]),
  [P.bedNo]: allData?.[P.bedNo] ? allData?.[P.bedNo] : initValue(allData?.[P.bedNoTransfer]),
  [P.date]: allData?.[P.date_time] ? moment(allData?.[P.date_time]) : moment(),
  [P.time]: allData?.[P.date_time] ? moment(allData?.[P.date_time]) : moment(),
  [P.admissionType]: initValue(allData?.[P.admissionType]),
  [P.principleDiagnosis]: Array.isArray(allData?.[P.principleDiagnosis]) ? allData?.[P.principleDiagnosis] : [],
  [P.principleDiagnosisText]: initValue(allData?.[P.principleDiagnosisText]),
  [P.principleDiagnosisInputValue]: '',
  [P.onVentilator]: initValue(allData?.[P.onVentilator]),
  [P.egPercent]: initValue(allData?.[P.egPercent]),
  [P.onOxygen]: allData?.[P.onOxygen],
  [P.tracheotomy]: allData?.[P.tracheotomy],
  [P.egL]: initValue(allData?.[P.egL]),
  [P.airbornePrecaution]: allData?.[P.airbornePrecaution],
  [P.dropletPrecaution]: allData?.[P.dropletPrecaution],
  [P.contactPrecaution]: allData?.[P.contactPrecaution],
  [P.reverseLsolation]: allData?.[P.reverseLsolation],
  [P.attention]: initValue(allData?.[P.attention]),
  [P.remarks]: handleInitRemark(allData),
  [P.status]: handleInitStatus(allData, bsMlookupList),
  [P.covid19]: initValue(allData?.[P.covid19], 0),
  [P.covidDate]: allData?.[P.covidReportDtm] ? moment(allData?.[P.covidReportDtm]) : moment(),
  [P.counter]: allData?.[P.covidReportDtm]
    ? Math.round((moment().valueOf() - allData?.[P.covidReportDtm]) / (24 * 60 * 60 * 1000))
    : 0,
  [P.wardCd]: initValue(allData?.[P.wardCd]),
  [P.assignedBedNo]: initValue(allData?.[P.assignedBedNo]),
  [P.destHospCode]: initValue(allData?.[P.destHospCode]),
  [P.destWard]: initValue(allData?.[P.destWard]),
  [P.destSpecialty]: initValue(allData?.[P.destSpecialty]),
  [P.destWardId]: initValue(allData?.[P.destWardId]),
  [P.hkid]: initValue(allData?.[P.hkid]),
  [P.chiName]: initValue(allData?.[P.chiName]),
  [P.destWardLocation]: initValue(allData?.[P.destWardLocation]),
  [P.cubicleId]: initValue(allData?.[P.cubicleId]),
});

// 生成当前表单使用的 Yup 校验规则。
export const buildTransferInSchema = (BookingDataFormat, options = {}) => ({
  [P.patienID]: Yup.string().test(
    'either-patientID-or-hkid',
    'Either Case No. or HKID / Doc. No. must be filled in.',
    function (value) {
      const hkidVal = BookingDataFormat?.[P.hkid];
      const hasValue = value && value.trim().length > 0;
      const hasHkid = hkidVal && hkidVal.trim().length > 0;
      if (hasValue) return true;
      if (hasHkid) return true;

      return false;
    }
  ),
  [P.hkid]: Yup.string().test(
    'either-patientID-or-hkid',
    'Either Case No. or HKID / Doc. No. must be filled in.',
    function (value) {
      const patientIDVal = BookingDataFormat?.[P.patienID];
      const hasValue = value && value.trim().length > 0;
      const hasPatientID = patientIDVal && patientIDVal.trim().length > 0;
      if (hasValue) return true;
      if (hasPatientID) return true;

      return false;
    }
  ),
  [P.hospitalCode]: Yup.string()
    .max(10)
    .required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_HOSPITAL_CODE') })),
  [P.name]: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_NAME') })),
  [P.age]: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_AGE') })),
  [P.specialtyCode]: Yup.string().required(
    i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_SPECIALTY_CODE') })
  ),
  [P.gender]: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_GENDER') })),
  [P.date]: Yup.date()
    .required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_DATE') }))
    .typeError(i18n.t('BEDSTATUS_INVALID_DATE_FORMAT')),
  [P.time]: Yup.date()
    .required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_TIME') }))
    .typeError(i18n.t('BEDSTATUS_INVALID_DATE_FORMAT')),
  [P.admissionType]: Yup.string().required(
    i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_ADMISSION_TYPE') })
  ),
  [P.status]: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_STATUS') })),
  [P.attention]: Yup.string().max(255).nullable(),
  [P.remarks]: Yup.string().max(255).nullable(),
  [P.destHospCode]: options.allowEmptyDestination
    ? Yup.string().nullable()
    : Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_DEST_HOSPITAL') })),
  [P.destWardId]: options.allowEmptyDestination
    ? Yup.string().nullable()
    : Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_DEST_WARD') })),
  [P.chiName]: Yup.string().nullable(),
  [P.destWardLocation]: options.allowEmptyDestination
    ? Yup.string().nullable()
    : Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: 'Dest. Ward Location' })),
});

// 将 retrieve 回来的病人资料转换成表单字段。
export const buildPatientRetrieveFormData = (PatientRetrieve, bsAttentionList) => ({
  [P.hospitalCode]: PatientRetrieve?.ccHosp ?? '',
  [P.ward]: initValue(PatientRetrieve?.ccWardLocation),
  [P.bedNo]: initValue(PatientRetrieve?.bedNo),
  [P.specialtyCode]: initValue(PatientRetrieve?.[P.specialtyCode]),
  [P.patienID]: initValue(PatientRetrieve?.[P.patienID]),
  [P.gender]: PatientRetrieve?.[P.gender],
  [P.name]: initValue(PatientRetrieve?.[P.name]),
  [P.chiName]: initValue(PatientRetrieve?.[P.chiName]),
  [P.age]: initValue(PatientRetrieve?.[P.age]),
  // [P.date]: moment(PatientRetrieve?.[P.admDtm]).isValid() ? moment(PatientRetrieve?.[P.admDtm]) : null,
  // [P.time]: moment(PatientRetrieve?.[P.admDtm]).isValid() ? moment(PatientRetrieve?.[P.admDtm]) : null,
  [P.attention]: bsAttentionFunc.getSortNameStr(bsAttentionList, PatientRetrieve?.[P.attention]),
  [P.reverseLsolation]: PatientRetrieve?.[P.reverseLsolation],
  [P.dropletPrecaution]: PatientRetrieve?.[P.dropletPrecaution],
  [P.airbornePrecaution]: PatientRetrieve?.[P.airbornePrecaution],
  [P.contactPrecaution]: PatientRetrieve?.[P.contactPrecaution],
  [P.covid19]: PatientRetrieve?.[P.covid19],
  [P.covidDate]: initMoment(PatientRetrieve?.[P.covidPositivePat]?.[0]?.[P.covidDate]),
  [P.counter]: Math.round(
    (moment().valueOf() - PatientRetrieve?.[P.covidPositivePat]?.[0]?.[P.covidDate]) / (24 * 60 * 60 * 1000)
  ),
  [P.remarks]: initRemark(PatientRetrieve?.[P.covidPositivePat]),
});

// 校验 retrieve 后更新到表单的病人资料。
export const validatePatientRetrieveFormData = async (newTemp) => {
  try {
    const schema = buildTransferInSchema(newTemp);
    const temp = initTempSchema(newTemp, schema);
    await checkSubmitData(Yup.object().shape(temp), newTemp);
    const errorTemp = {};
    Object.keys(temp).forEach((item) => {
      if (temp[item]) {
        errorTemp[item] = null;
      }
    });
    return errorTemp;
  } catch (e) {
    return e;
  }
};

// 判断 retrieve 是否返回了可用于提示成功的资料。
export const hasPatientRetrieveData = (PatientRetrieve, newTemp) => {
  return Object.keys(newTemp).find((item) => {
    return PatientRetrieve[item] !== null;
  });
};

// 校验 retrieve 前需要的病人识别资料。
export const validateRetrievePatientIdentity = async (BookingDataFormat, schema) => {
  if (!BookingDataFormat[P.hkid]) {
    await checkSubmitData(
      Yup.object().shape({ [P.patienID]: schema[P.patienID], [P.hkid]: schema[P.hkid] }),
      BookingDataFormat
    );
  }
};

// 生成 transfer-in popup 提交给外层保存方法的资料。
export const buildTransferInSubmitData = async ({
  allData,
  BookingDataFormat,
  destUnitName,
  patientKeyVal,
  deletedStatus,
}) => ({
  ...allData,
  ...BookingDataFormat,
  [P.age]: Number(BookingDataFormat[P.age]),
  [P.attention]: BookingDataFormat[P.attention],
  [P.remarks]: BookingDataFormat[P.remarks],
  [P.date_time]: moment(
    moment(BookingDataFormat[P.date], 'D/MM/yyyy').format('yyyy-MM-DD ') + BookingDataFormat[P.time].format('HH:mm:ss'),
    'yyyy-MM-DD HH:mm:ss'
  ).valueOf(),
  [P.covidReportDtm]: BookingDataFormat[P.covid19] ? moment(BookingDataFormat[P.covidDate]).valueOf() : null,
  [P.destWard]: destUnitName,
  [P.patientKey]: patientKeyVal,
  [P.status]: initValue(deletedStatus, BookingDataFormat[P.status]),
  [P.hkid]: await encryptHkid(BookingDataFormat[P.hkid]),
});

// 校验失焦字段并返回需要清理的错误。
export const validateBlurField = async ({ name, schema, BookingDataFormat }) => {
  if ([P.patienID, P.hkid].includes(name)) {
    await checkSubmitData(Yup.object().shape({ [P.patienID]: schema[P.patienID], [P.hkid]: schema[P.hkid] }), {
      [P.patienID]: BookingDataFormat[P.patienID],
      [P.hkid]: BookingDataFormat[P.hkid],
    });
    return { [P.patienID]: null, [P.hkid]: null };
  }
  await checkSubmitData(Yup.object().shape({ [name]: schema[name] }), { [name]: BookingDataFormat[name] });
  return { [name]: null };
};

// 将输入值转换成整数，非法输入保持为空。
export const parseIntegerInput = (value) => {
  const temp = parseInt(value);
  return isNaN(temp) ? '' : temp;
};

// 校验日期字段并返回需要合并的错误状态。
export const validateDateField = async ({ key, schema, value }) => {
  try {
    await checkSubmitData(Yup.object().shape({ [key]: schema[key] }), { [key]: value });
    return { [key]: null };
  } catch (e) {
    return e;
  }
};
